/**
 * /policies/payroll/deductions-catalog — Session 3c PR 15.
 * Owning agent: Rawatib.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { DeductionCodeItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function PayrollDeductionsCatalogPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-payroll-deductions", le],
    queryFn: () => session3cApi.listDeductions(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<DeductionCodeItem>[] = [
    {
      headerKey: "policies.payroll_deductions.col.code",
      label: "Code",
      render: (r) => <span className="font-mono text-xs">{r.code}</span>,
    },
    {
      headerKey: "policies.payroll_deductions.col.name",
      label: "Name",
      render: (r) => <span className="font-medium">{r.name_en}</span>,
    },
    {
      headerKey: "policies.payroll_deductions.col.category",
      label: "Category",
      render: (r) => <span className="text-xs text-text-secondary">{r.category}</span>,
    },
    {
      headerKey: "policies.payroll_deductions.col.cap",
      label: "Article 90 cap-counted",
      render: (r) => (
        <span className={`text-xs ${r.article_90_cap_counted ? "font-semibold text-purple-800" : "text-text-tertiary"}`}>
          {r.article_90_cap_counted ? "yes" : "no"}
        </span>
      ),
    },
    {
      headerKey: "policies.payroll_deductions.col.mandatory",
      label: "Type",
      render: (r) => <span className="text-xs">{r.is_mandatory ? "mandatory" : "discretionary"}</span>,
    },
  ];

  return (
    <PolicyListPage<DeductionCodeItem>
      title={t("policies.payroll_deductions.title", "Deductions Catalog")}
      subtitle={t("policies.payroll_deductions.subtitle", "All deduction codes — GOSI · tax · loans · garnishments · unpaid leave · ad-hoc. Mutaba'aa flags Article 90-counted codes.")}
      agent="rawatib"
      domain="payroll_deductions"
      newButtonLabel={t("policies.payroll_deductions.cta.new", "New Deduction Code")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.payroll_deductions.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.payroll_deductions.suggestion.clean_body", "Cap-counted deductions inherit Article 90 cap automatically. The cap config lives on /policies/loans/article-90-config."),
          primaryActionLabel: t("policies.payroll_deductions.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "payroll-deductions-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.payroll_deductions.title", "Deductions Catalog"),
        window_label: t("policies.payroll_deductions.window", "active codes"),
      }}
    />
  );
}
